import {translateFunction} from "../../../../localization/TranslationService";
import {Alert, Button, Paper, TableBody, TableFooter, TableHead, TextField, Typography} from "@mui/material";
import React, {useState} from "react";
import {CustomAttributeSetup} from "../../models/CustomAttributeSetup";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import {BulkRowProps} from "../models/BulkRowProps";
import {ProductSupplier} from "../../models/ProductSupplier";
import {graphQLServices} from "../../../../services/GraphQLServices";
import {SUPPLIER_CUSTOM_ATTRIBUTE_BULK_UPDATE} from "../../Queries";
import {productsEditorDefaultTargetStage, productsEditorTargetStage} from "../../ProductModuleOverview";
import {CustomAttributeBulkUpdateInput} from "../../models/CustomAttributeBulkUpdateInput";
import {CustomAttributeBulkUpdateBatchInput} from "../../models/CustomAttributeBulkUpdateBatchInput";
import {GlobalState} from "../../../../global/GlobalState";

const translate = translateFunction("SureBasePortalProductsCatalog");

export interface CustomAttributeBulkEditorSuppliersProps {
	suppliers: ProductSupplier[];
	customAttributes: CustomAttributeSetup[];
	
}

export function CustomAttributeBulkEditorSuppliers(props: CustomAttributeBulkEditorSuppliersProps) {
	const stage = GlobalState.targetKiziStage(productsEditorDefaultTargetStage);
	const [suppliers, setSuppliers] = useState<ProductSupplier[]>();
	const [valueLookup, setValueLookup] = useState<Map<string, string>>();
	const [rows, setRows] = useState<BulkRowProps[]>();
	const [isSaveSuccessful, setIsSaveSuccessful] = useState(false);
	
	if (props.suppliers === null || props.suppliers.length === 0)
		throw new Error(`Suppliers not found.`);
	
	if (!suppliers)
		setSuppliers(props.suppliers);
	
	if (props.customAttributes && suppliers && !valueLookup) {
		const values = createValueLookup(suppliers);
		const bulkRows = createBulkRows(props.customAttributes, suppliers, values);
		setValueLookup(values);
		setRows(bulkRows);
	}
	
	function createValueLookup(suppliers: ProductSupplier[]): Map<string, string> {
		const result = new Map<string, string>();
		for (const product of suppliers) {
			if (product.customAttributes) {
				for (const customAttribute of product.customAttributes) {
					result.set(product.krn + customAttribute.description, customAttribute.value);
				}
				
			}
		}
		return result;
	}
	
	function createCustomAttributeBulkUpdateInput(records: BulkRowProps[]): CustomAttributeBulkUpdateBatchInput {
		const result: CustomAttributeBulkUpdateBatchInput = {
			items: []
		};
		
		for (const record of records) {
			if (!record.dirty)
				continue;
			
			const product: CustomAttributeBulkUpdateInput = {
				krn: record.krn,
				customAttributes: []
			};
			if (record.cells) {
				
				for (const cell of record.cells) {
					if (!cell.value || cell.value === "")
						continue;
					product.customAttributes.push({
						value: cell.value,
						description: cell.column
					});
				}
			}
			result.items.push(product);
		}
		
		return result;
	}
	
	async function saveChanges() {
		if (!rows || rows.length === 0)
			return;
		const batch = createCustomAttributeBulkUpdateInput(rows);
		if (batch.items.length === 0)
			return;
		await graphQLServices.productsClient.mutate({
			mutation: SUPPLIER_CUSTOM_ATTRIBUTE_BULK_UPDATE,
			variables: {
				stage: productsEditorTargetStage,
				batch: batch,
			}
		});
		setIsSaveSuccessful(true);
	}
	
	function createBulkRows(attributes: CustomAttributeSetup[], suppliers: ProductSupplier[], valueLookup: Map<string, string>): BulkRowProps[] {
		const result: BulkRowProps[] = [];
		for (const supplier of suppliers) {
			const row: BulkRowProps = {
				cells: [],
				id: supplier.krn,
				krn: supplier.krn,
				description: supplier.legalName ?? "",
				dirty: false
			};
			for (const attribute of attributes) {
				const key = supplier.krn + attribute.description;
				if (valueLookup.has(key))
					row.cells.push({
						id: attribute.id,
						column: attribute.description,
						value: valueLookup.get(key) ?? ""
					});
				else
					row.cells.push({
						id: attribute.id,
						column: attribute.description,
						value: undefined
					});
			}
			result.push(row);
		}
		
		return result;
	}
	
	const handleCloseAlert = () => {
		setIsSaveSuccessful(false);
	};
	
	return (
		<>
			{isSaveSuccessful && <Alert
				severity="success"
				onClose={handleCloseAlert}
			>
				{translate("Successfully saved")}
			</Alert>}
			<TableContainer component={Paper}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>
								<Typography>Krn</Typography>
							</TableCell>
							<TableCell>
								<Typography>Supplier</Typography>
							</TableCell>
							{props.customAttributes?.map((header, index) => (
								<TableCell key={index}>
									<Typography>{header.description}</Typography>
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{rows?.map((row, rowIndex) => (
							<TableRow key={rowIndex}>
								<TableCell>
									<Typography>{row.krn}</Typography>
								</TableCell>
								<TableCell>
									<Typography>{row.description}</Typography>
								</TableCell>
								{row.cells.map((cell, cellIndex) => (
									<TableCell key={cellIndex}>
										<TextField
											style={{width: "500px"}}
											id={cell.id}
											size={"small"}
											value={cell.value}
											multiline
											placeholder={cell.column}
											onChange={event => {
												const clone = [...rows];
												clone[rowIndex].cells[cellIndex].value = event.target.value;
												clone[rowIndex].dirty = true;
												setRows(clone);
											}}
										/>
									</TableCell>
								))}
							</TableRow>
						))}
					</TableBody>
					<TableFooter>
						<TableRow>
							<TableCell>
								{stage === productsEditorTargetStage &&
									<Button onClick={saveChanges} color={"success"}>{translate("Save changes")}</Button>
								}
								{stage !== productsEditorTargetStage &&
									<Typography>{translate("Editing only allowed on Dev")}</Typography>
								}
							</TableCell>
						</TableRow>
					</TableFooter>
				</Table>
			</TableContainer>
		</>
	)
	;
}
